/**
 * The citizen inbox page's state (F50): which slice is open, which message is
 * being read, and the actions on it. Every decision about a message — what it
 * says, whether it is read, whether its copy was retained — comes from the
 * server; this hook only wires the page to it.
 *
 * ─── The URL is the state ─────────────────────────────────────────────────
 *
 * `?view=unread|archived` and `?message=<id>` — so a view is bookmarkable, the
 * browser's Back closes an open message (on a phone, where the reader replaces
 * the list), and the notification bell can open one message directly.
 *
 * ─── One unread number ────────────────────────────────────────────────────
 *
 * The count is `useInboxUnreadCount`, the query the bell reads, so the two can
 * never show different numbers.
 *
 * ─── The reader is addressed by id ────────────────────────────────────────
 *
 * `useInboxItem(?message)`, not a lookup in the open view's list: reading a
 * message moves it out of Unread, and the reader must stay open while it does.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from '@tonnie/locale';
import {
  useInbox,
  useInboxAction,
  useInboxItem,
  useInboxUnreadCount,
  useMarkAllInboxRead,
  type InboxAction,
} from '@tonnie/sdk';
import type { InboxItem, InboxView } from '@tonnie/types';
import { toast } from 'sonner';
import { useLocation, useSearch } from 'wouter';

import { groupInboxByDay, type InboxDaySection } from '@tonnie/heroes';

const VIEWS: readonly InboxView[] = ['inbox', 'unread', 'archived'];

function parseView(raw: string | null): InboxView {
  return VIEWS.includes(raw as InboxView) ? (raw as InboxView) : 'inbox';
}

/** The inbox URL for a view and (optionally) one open message. */
export function inboxHref(view: InboxView, messageId?: string | null): string {
  const params = new URLSearchParams();
  if (view !== 'inbox') params.set('view', view);
  if (messageId) params.set('message', messageId);
  const query = params.toString();
  return query ? `/inbox?${query}` : '/inbox';
}

export interface InboxPageState {
  view: InboxView;
  setView: (view: InboxView) => void;
  items: InboxItem[];
  sections: InboxDaySection[];
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  /** A message is open (`?message=`) — loaded, loading or missing. */
  readerOpen: boolean;
  selected: InboxItem | null;
  /** The open message is still on its way (opened from a link, not a list). */
  selectedLoading: boolean;
  /** A `?message=` that is not the person's, or no longer exists. */
  selectedMissing: boolean;
  /** Open a message — and, if it is unread, record that the person read it. */
  open: (item: InboxItem) => void;
  close: () => void;
  archive: (item: InboxItem) => void;
  restore: (item: InboxItem) => void;
  /** Mark it unread; the reader closes, as mail apps do, with Undo. */
  markUnread: (item: InboxItem) => void;
  markAllRead: () => void;
  isMarkingAllRead: boolean;
  isActing: boolean;
}

export function useInboxPageState(): InboxPageState {
  const { t } = useTranslation();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const view = parseView(params.get('view'));
  const selectedId = params.get('message');

  const query = useInbox(view);
  const unread = useInboxUnreadCount();
  const action = useInboxAction();
  const markAll = useMarkAllInboxRead();

  const items = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.items),
    [query.data]
  );
  const sections = useMemo(() => groupInboxByDay(items, new Date()), [items]);
  const opened = useInboxItem(selectedId);
  const selected = selectedId !== null ? (opened.data ?? null) : null;
  const selectedMissing = selectedId !== null && opened.isError;

  // The one unread number; the page's own count stands in only until it lands.
  const unreadCount = unread.data ?? query.data?.pages[0]?.unreadCount ?? 0;

  // A message opened from the URL (the bell, a bookmark, Back) is read too —
  // once per OPENING, decided the first time the reader holds it. Not "whenever
  // it shows unread": "Mark unread" clears `readAt` optimistically while the
  // reader is still open, and reading it again then sent `read` right behind
  // `unread` — whichever reached the server last won, and the second mutate
  // dropped the unread's own onSuccess, so the reader never closed.
  const openedId = useRef<string | null>(null);
  const { mutate } = action;
  useEffect(() => {
    if (selectedId === null) {
      openedId.current = null;
      return;
    }
    if (!selected || openedId.current === selected.id) return;
    openedId.current = selected.id;
    if (!selected.readAt) mutate({ id: selected.id, action: 'read' });
  }, [selectedId, selected, mutate]);

  const go = useCallback(
    (nextView: InboxView, messageId?: string | null) => setLocation(inboxHref(nextView, messageId)),
    [setLocation]
  );

  const run = useCallback(
    (item: InboxItem, next: InboxAction, done?: () => void) =>
      mutate(
        { id: item.id, action: next },
        {
          onSuccess: done,
          onError: () => toast.error(t('inbox.actionError')),
        }
      ),
    [mutate, t]
  );

  return {
    view,
    setView: (next) => go(next),
    items,
    sections,
    unreadCount,
    isLoading: query.isLoading,
    isError: query.isError,
    retry: () => void query.refetch(),
    hasMore: Boolean(query.hasNextPage),
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    readerOpen: selectedId !== null,
    selected,
    selectedLoading: selectedId !== null && !selected && !opened.isError,
    selectedMissing,
    open: (item) => go(view, item.id),
    close: () => go(view),
    archive: (item) =>
      run(item, 'archive', () => {
        // It leaves this slice; the reader closes with it, and Undo brings it back.
        go(view);
        toast(t('inbox.archived'), {
          action: { label: t('inbox.undo'), onClick: () => run(item, 'restore') },
        });
      }),
    restore: (item) =>
      run(item, 'restore', () => {
        go(view);
        toast(t('inbox.restored'));
      }),
    markUnread: (item) =>
      run(item, 'unread', () => {
        // Closing ends the opening: opening it again reads it again.
        go(view);
        toast(t('inbox.markedUnread'), {
          action: { label: t('inbox.undo'), onClick: () => run(item, 'read') },
        });
      }),
    markAllRead: () =>
      markAll.mutate(undefined, {
        onSuccess: () => toast(t('inbox.markAllReadDone')),
        onError: () => toast.error(t('inbox.actionError')),
      }),
    isMarkingAllRead: markAll.isPending,
    isActing: action.isPending,
  };
}
